import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Avatar, Button, Empty, Input, Skeleton, Upload, message } from 'antd';
import { DownOutlined, MoreOutlined, PictureOutlined, PlusCircleOutlined, UserOutlined } from '@ant-design/icons';
import PageTopbar from '@/components/pagetopbar';
import UserProfileLink from '@/components/userprofilelink';
import { useNavigate } from 'react-router-dom';
import { fetchConversation, fetchMessages, sendMessage, markConversationRead, uploadChatImage, deleteChatImage, fetchChatImageQuota } from '@/apis/chat';
import { getPostErrorMessage } from '@/apis/posts';
import { mergeMessages, conversationTitle } from '@/utils/chat';
import MembersModal from './MembersModal';
import ChatImage from './ChatImage';
import { validateImageFile } from '@/components/imagepicker';
import ImageQuota from '@/components/imagequota';
import useChatEvents from '@/utils/useChatEvents';
import ChatInformation from './ChatInformation';
import { fetchMessageDelta, newMessageId } from '@/utils/chatCursor';

const MESSAGE_MAX_LENGTH = 2000;

const ChatThread = ({ id, userId, onRead, onConversationRemoved }) => {
  const navigate = useNavigate();
  const [room, setRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pollError, setPollError] = useState('');
  const olderCursor = useRef(null);
  const [hasOlder, setHasOlder] = useState(false);
  const [historyBusy, setHistoryBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [content, setContent] = useState('');
  const [attachment, setAttachment] = useState(null);
  const attachmentRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const logicalSend = useRef(null);
  const uploadLock = useRef(false);
  const [membersOpen, setMembersOpen] = useState(false);
  const [memberInvite, setMemberInvite] = useState(false);
  const [informationOpen, setInformationOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [toolsReady, setToolsReady] = useState(false);
  const composerInput = useRef(null);
  const [retry, setRetry] = useState(0);
  const [bottomVisible, setBottomVisible] = useState(true);
  const [visible, setVisible] = useState(!document.hidden);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 480);
  const scroller = useRef(null);
  const newestId = useRef(0);
  const readId = useRef(0);
  const sendBusy = useRef(false);
  const composing = useRef(false);
  const historyLock = useRef(false);
  const scrollOnUpdate = useRef(true);
  const mounted = useRef(false);
  const refreshHint = useRef(() => {});
  const unavailable = useRef(false);
  const historyVersion = useRef(0);
  const resetHistory = useCallback(() => {
    historyVersion.current += 1;
    newestId.current = 0; readId.current = 0; olderCursor.current = null;
    setHasOlder(false); setMessages([]); setRoom(null); setMembersOpen(false); setInformationOpen(false); setBottomVisible(true);
  }, []);
  useChatEvents(id, () => refreshHint.current(), () => { unavailable.current = true; setError('会话已不可用，你可能已退出或群聊已解散'); setMessages([]); setRoom(null); onRead(Number(id)); });
  const latestId = Math.min(messages[messages.length - 1]?.id || 0, newestId.current);
  useEffect(() => {
    const resize = () => setIsMobile(window.innerWidth <= 480);
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);
  useEffect(() => {
    mounted.current = true;
    const handleVisibility = () => setVisible(!document.hidden);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => { mounted.current = false; document.removeEventListener('visibilitychange', handleVisibility); };
  }, []);

  useEffect(() => {
    let active = true;
    let polling = false;
    historyVersion.current += 1;
    newestId.current = 0; readId.current = 0; unavailable.current = false; scrollOnUpdate.current = true;
    setLoading(true); setError(''); setPollError(''); setRoom(null); setMessages([]);
    setMembersOpen(false); setInformationOpen(false); olderCursor.current = null; setBottomVisible(true);
    const load = async initial => {
      if (polling || unavailable.current || (!initial && document.hidden)) return;
      polling = true;
      const version = historyVersion.current;
      try {
        const conversation = await fetchConversation(id);
        if (!active || version !== historyVersion.current) return;
        if (!conversation?.id) throw new Error('会话响应无效');
        let updates;
        if (initial || !newestId.current) {
          const result = await fetchMessages(id, { pagination: 'cursor', pageSize: 50 });
          if (!active || version !== historyVersion.current) return;
          if (!Array.isArray(result?.data)) throw new Error('消息响应无效');
          updates = result.data;
          olderCursor.current = result.pagination.next_before_id;
          setHasOlder(Boolean(result.pagination.has_more));
        } else {
          updates = await fetchMessageDelta(params => fetchMessages(id, params), newestId.current, () => active && version === historyVersion.current);
        }
        if (!active || version !== historyVersion.current) return;
        setRoom(conversation); setError('');
        newestId.current = Math.max(newestId.current, ...updates.map(item => item.id));
        setMessages(current => mergeMessages(current, updates));
        setPollError('');
      } catch (err) {
        if (active && version === historyVersion.current) {
          const text = getPostErrorMessage(err, '会话加载失败，你可能已不在该会话中');
          if ([403, 404].includes(err.response?.status)) { unavailable.current = true; setRoom(null); setMessages([]); setError(text); onRead(Number(id)); }
          else if (err.response?.status === 409) { resetHistory(); setError('聊天记录的可见范围已变化，请重新加载'); onRead(Number(id)); }
          else if (initial) setError(text); else setPollError(text);
        }
      } finally { polling = false; if (active && initial) setLoading(false); }
    };
    refreshHint.current = () => load(false);
    load(true);
    const timer = setInterval(() => load(false), process.env.REACT_APP_CHAT_WEBSOCKET_ENABLED === 'true' ? 30000 : 8000);
    return () => { active = false; historyVersion.current += 1; refreshHint.current = () => {}; clearInterval(timer); };
  }, [id, userId, retry, onRead, resetHistory]);

  useEffect(() => {
    if (scrollOnUpdate.current && scroller.current) scroller.current.scrollTop = scroller.current.scrollHeight;
  }, [messages, toolsOpen, attachment, isMobile, content]);
  useEffect(() => {
    if (!latestId || !bottomVisible || !visible || readId.current >= latestId) return;
    let active = true;
    markConversationRead(id, latestId).then(() => {
      if (active) { readId.current = latestId; onRead(Number(id)); }
    }).catch(err => { if (active) setPollError(getPostErrorMessage(err, '已读状态同步失败')); });
    return () => { active = false; };
  }, [id, latestId, bottomVisible, visible, onRead]);

  const loadOlder = async () => {
    if (historyLock.current) return;
    historyLock.current = true; setHistoryBusy(true);
    const container = scroller.current;
    const height = container?.scrollHeight || 0;
    const top = container?.scrollTop || 0;
    const version = historyVersion.current;
    try {
      const result = await fetchMessages(id, { before_id: olderCursor.current, pageSize: 50 });
      if (!mounted.current || version !== historyVersion.current) return;
      scrollOnUpdate.current = false;
      setMessages(current => mergeMessages(current, result.data));
      olderCursor.current = result.pagination.next_before_id; setHasOlder(Boolean(result.pagination.has_more));
      requestAnimationFrame(() => { if (container && mounted.current) container.scrollTop = top + container.scrollHeight - height; });
    } catch (err) { if (mounted.current && version === historyVersion.current) {
      if (err.response?.status === 409) { resetHistory(); setError('聊天记录的可见范围已变化，请重新加载'); }
      else message.error(getPostErrorMessage(err, '历史消息加载失败'));
    } }
    finally { historyLock.current = false; if (mounted.current) setHistoryBusy(false); }
  };
  useEffect(() => () => {
    const pending = attachmentRef.current;
    if (pending && !sendBusy.current) deleteChatImage(id, pending.id).catch(() => {});
  }, [id]);
  const chooseImage = async file => {
    if (uploadLock.current || sendBusy.current || attachmentRef.current) return false;
    const validation = validateImageFile(file, 5);
    if (validation) { message.warning(validation); return false; }
    uploadLock.current = true; setUploading(true);
    try {
      const result = await uploadChatImage(id, file);
      if (!result?.id || !result.content_url) throw new Error('图片上传响应无效');
      if (!mounted.current) { await deleteChatImage(id, result.id); return false; }
      attachmentRef.current = result; setAttachment(result); logicalSend.current = null;
      setToolsOpen(false);
    } catch (err) { if (mounted.current) message.error(getPostErrorMessage(err, '图片上传失败')); }
    finally { uploadLock.current = false; if (mounted.current) setUploading(false); }
    return false;
  };
  const cancelImage = async () => {
    if (sendBusy.current || uploadLock.current || !attachmentRef.current) return;
    uploadLock.current = true;
    setUploading(true);
    try { await deleteChatImage(id, attachmentRef.current.id); attachmentRef.current = null; setAttachment(null); logicalSend.current = null; }
    catch (err) { message.error(getPostErrorMessage(err, '图片取消失败')); }
    finally { uploadLock.current = false; if (mounted.current) setUploading(false); }
  };
  const send = async () => {
    if (sendBusy.current || uploadLock.current || room?.dissolved_at || (!attachment && !content.trim())) return;
    const body = attachment ? { message_type: 'image', attachment_id: attachment.id } : { content: content.trim(), message_type: 'text' };
    const signature = JSON.stringify(body);
    if (logicalSend.current?.signature !== signature) logicalSend.current = { signature, payload: { ...body, client_message_id: newMessageId() } };
    sendBusy.current = true; setSending(true);
    try {
      const result = await sendMessage(id, logicalSend.current.payload);
      if (!result?.id) throw new Error('消息响应无效');
      // 即使用户已离开页面，也不能删除已发送附件。
      if (attachment) attachmentRef.current = null;
      logicalSend.current = null;
      if (!mounted.current) return;
      scrollOnUpdate.current = true; setBottomVisible(true);
      setMessages(current => mergeMessages(current, [result]));
      if (attachment) setAttachment(null); else setContent('');
    } catch (err) { if (mounted.current) message.error(getPostErrorMessage(err, '消息发送失败，可重试发送')); }
    finally { sendBusy.current = false; if (mounted.current) setSending(false); }
  };
  const handleMessageKeyDown = event => {
    if (isMobile) return;
    if (event.key !== 'Enter' || composing.current || event.nativeEvent.isComposing || event.keyCode === 229) return;
    event.preventDefault();
    if (event.repeat || sendBusy.current) return;
    if (!event.ctrlKey) {
      send();
      return;
    }
    const input = event.currentTarget;
    const { selectionStart, selectionEnd } = input;
    const next = content.slice(0, selectionStart) + '\n' + content.slice(selectionEnd);
    if (next.length > MESSAGE_MAX_LENGTH) return;
    setContent(next);
    requestAnimationFrame(() => {
      if (mounted.current && input.isConnected) input.setSelectionRange(selectionStart + 1, selectionStart + 1);
    });
  };
  const title = `${room ? conversationTitle(room, userId) : '聊天'}${room?.kind === 'group' ? `(${room.members?.length || 0})` : ''}`;
  const moreButton = room && <Button className="chat-thread-more" type="text" icon={<MoreOutlined rotate={90} />} aria-label="聊天信息" onClick={() => setInformationOpen(true)} />;
  const heading = isMobile
    ? <PageTopbar className="chat-thread-mobile-heading" title={title} backLabel="返回会话列表" onBack={() => navigate('/message')} actions={moreButton} />
    : <div className="chat-thread-heading"><div className="chat-thread-title"><h2>{title}</h2></div>{moreButton}</div>;
  if (loading) return <div className="chat-thread">{isMobile && heading}<Skeleton active /></div>;
  if (error) return <div className="chat-thread">{isMobile && heading}<Alert type="error" showIcon title={error} action={<Button onClick={() => setRetry(value => value + 1)}>重试</Button>} /></div>;
  return <div className="chat-thread" onClickCapture={event => {
    if (!event.target.closest('.chat-composer')) setToolsOpen(false);
  }}>
    {heading}
    {pollError && <Alert type="warning" showIcon title={pollError} action={<Button onClick={() => setRetry(value => value + 1)}>重试</Button>} />}
    <div className="chat-thread-timeline">
    <div className="chat-thread-messages" ref={scroller} onScroll={event => { const near = event.currentTarget.scrollHeight - event.currentTarget.scrollTop - event.currentTarget.clientHeight < 48; scrollOnUpdate.current = near; setBottomVisible(near); }}>
      {hasOlder && <Button className="chat-history" size="small" loading={historyBusy} onClick={loadOlder}>查看更早的消息</Button>}
      {!messages.length && <Empty description="还没有消息，打个招呼吧" />}
      {messages.map(item => item.message_type === 'system' ? <p className="chat-system" key={item.id}>{item.content}</p> : <article key={item.id} className={`chat-message ${item.sender?.id === userId ? 'is-mine' : ''}`}>
        <UserProfileLink user={item.sender}><Avatar src={item.sender?.avatar || undefined} icon={<UserOutlined />} /></UserProfileLink><div><div className="chat-message-author">{item.sender?.nickname || item.sender?.username || '已注销用户'} <time>{new Date(item.created_at).toLocaleString('zh-CN')}</time></div>
          <div className={`chat-bubble${item.message_type === 'image' ? ' chat-bubble-image' : ''}`}>{item.message_type === 'image' && item.attachment ? <ChatImage attachment={item.attachment} /> : item.message_type === 'image' && /^https?:\/\//i.test(item.content) ? <a href={item.content} target="_blank" rel="noreferrer"><img src={item.content} alt="聊天图片" loading="lazy" /></a> : item.content}</div>
        </div>
      </article>)}
    </div>
    {!bottomVisible && <Button className="chat-latest" icon={<DownOutlined />} aria-label="回到最新消息" onClick={() => { scrollOnUpdate.current = true; setBottomVisible(true); scroller.current.scrollTop = scroller.current.scrollHeight; }}>最新消息</Button>}
    </div>
    {attachment && <div className="chat-attachment-preview"><ChatImage attachment={attachment} /><Button disabled={sending || uploading} onClick={cancelImage}>取消图片</Button></div>}
    <div className="chat-composer">
      <div className="chat-composer-entry">
        <Input.TextArea ref={composerInput} aria-label="消息内容" value={content} disabled={sending || Boolean(attachment) || Boolean(room?.dissolved_at)}
          onFocus={() => setToolsOpen(false)} onChange={event => { setContent(event.target.value); setToolsOpen(false); }} onKeyDown={handleMessageKeyDown}
          onCompositionStart={() => { composing.current = true; }} onCompositionEnd={() => { composing.current = false; }}
          enterKeyHint={isMobile ? 'enter' : 'send'} autoSize={{ minRows: 1, maxRows: 5 }} maxLength={MESSAGE_MAX_LENGTH}
          placeholder={isMobile ? '说点什么吧…' : '说点什么吧…（Enter 发送，Ctrl+Enter 换行）'} />
          {(Boolean(content.trim()) || attachment) && <Button className="chat-composer-send" type="primary" loading={sending}
            disabled={uploading || sending || Boolean(room?.dissolved_at)} onClick={send}>发送</Button>}
          {!content.trim() && !attachment && <Button className="chat-composer-more" type="text" icon={<PlusCircleOutlined />} loading={uploading}
            aria-label={toolsOpen ? '收起聊天工具' : '展开聊天工具'} aria-expanded={toolsOpen} aria-controls={`chat-tools-${id}`}
            disabled={sending || uploading || Boolean(room?.dissolved_at)} onClick={() => {
              if (!toolsOpen) { composerInput.current?.blur(); setToolsReady(true); }
              setToolsOpen(value => !value);
            }} />}
      </div>
      <div className={`chat-composer-tools${toolsOpen ? ' is-open' : ''}`} id={`chat-tools-${id}`}
        aria-hidden={!toolsOpen ? true : undefined} inert={!toolsOpen}>
        <Upload accept="image/jpeg,image/png,image/webp" showUploadList={false} beforeUpload={chooseImage} disabled={sending || uploading || Boolean(attachment) || Boolean(room?.dissolved_at)}>
          <Button className="chat-composer-image" icon={<PictureOutlined />} loading={uploading} disabled={sending || uploading || Boolean(attachment) || Boolean(room?.dissolved_at)}>图片</Button>
        </Upload>
        {toolsReady && <ImageQuota fetchQuota={fetchChatImageQuota} version={attachment?.id || 0} />}
      </div>
    </div>
    {room && informationOpen && <ChatInformation room={room} userId={userId} isMobile={isMobile} open={informationOpen} onClose={() => setInformationOpen(false)}
      onMembers={({ invite = false } = {}) => { setMemberInvite(invite); setInformationOpen(false); setMembersOpen(true); }} onMuteChange={values => setRoom(current => ({ ...current, ...values }))} onChanged={() => setRetry(value => value + 1)} onRemoved={onConversationRemoved} />}
    <MembersModal room={room} userId={userId} open={membersOpen} onClose={() => setMembersOpen(false)}
      onBack={() => { setMembersOpen(false); setInformationOpen(true); }} initialInvite={memberInvite}
      onChanged={() => setRetry(value => value + 1)} hideConversationActions onRemoved={onConversationRemoved} />
  </div>;
};
export default ChatThread;
